// TAIR3-1015: wording for the locus page's Update History lines.
//
// Each UpdateHistory row reaches the locus JSON (tair-to-nosql) from one of
// two sides, and the JSON names are the reverse of what they suggest:
//   - affectedByHistories: rows stored ON this locus (it is tair_object_id).
//     `name` is the other locus (affected_tair_object_id), if any.
//     updateType: added, obsoleted, obsolete and replaced by, split and added
//   - updateHistories: rows on ANOTHER locus that name this one as
//     affected_tair_object_id. `name` is the locus the row is on.
//     updateType: added from split, replaces
//
// A line is an array of segments, { text } or { text, link }, so locus names
// can link without building HTML from data.

// "A", "A and B" or "A, B and C", as names and separators interleaved.
function joinNames(names) {
  if (names.length <= 1) {
    return names.slice();
  }
  const parts = [];
  names.forEach((n, i) => {
    if (i > 0) {
      parts.push(i === names.length - 1 ? " and " : ", ");
    }
    parts.push(n);
  });
  return parts;
}

function locusLink(h) {
  if (h.tairObjectId) {
    return "/locus?accession=" + h.tairObjectId;
  }
  return "/locus?name=" + encodeURIComponent(h.name);
}

function locusSegment(h) {
  return { text: h.name, link: locusLink(h) };
}

function text(t) {
  return { text: t };
}

// Expands joinNames() output into segments, linking each name.
function nameListSegments(rows) {
  const byName = {};
  rows.forEach((h) => {
    byName[h.name] = h;
  });
  const names = Object.keys(byName).sort();
  return joinNames(names).map((part, i) =>
    i % 2 === 0 ? locusSegment(byName[part]) : text(part)
  );
}

// Rows stored on this locus.
function ownLine(page, h) {
  const date = h.date;
  switch (h.updateType) {
    case "added":
    case "obsoleted":
      return [text(`${page} ${h.updateType} on ${date}`)];
    case "obsolete and replaced by":
      // Curator review (TAIR3-1015): say that it was a merge.
      if (h.name) {
        return [
          text(`${page} merged into and replaced by `),
          locusSegment(h),
          text(` on ${date}`),
        ];
      }
      break;
    case "split and added":
      if (h.name) {
        return [
          text(`${page} created on ${date} by splitting `),
          locusSegment(h),
        ];
      }
      break;
  }
  return genericLine(page, h);
}

// Rows on another locus that name this one, other than splits.
function affectedLine(page, h) {
  if (h.updateType === "replaces" && h.name) {
    // Curator review (TAIR3-1015): the survivor names the locus merged into it.
    return [
      locusSegment(h),
      text(` was merged into this locus on ${h.date}`),
    ];
  }
  return genericLine(page, h);
}

// One line for every locus split off this one on the same date.
function splitLine(page, isObsolete, date, rows) {
  const names = nameListSegments(rows);
  if (isObsolete) {
    return [text(`${page} split into `), ...names, text(` on ${date}`)];
  }
  // Count loci, not rows: a duplicated row still names one locus.
  const count = new Set(rows.map((h) => h.name)).size;
  const lead =
    count === 1 ? "part of this locus is now " : "parts of this locus are now ";
  return [text(`${page} split on ${date}; ${lead}`), ...names];
}

// Types without their own wording keep the old "<page> <type> <name> on <date>".
function genericLine(page, h) {
  const line = [text(`${page} ${h.updateType} `)];
  if (h.name) {
    line.push(locusSegment(h), text(" "));
  }
  line.push(text(`on ${h.date}`));
  return line;
}

function lineKey(line) {
  return line.map((s) => s.text).join("");
}

// Returns the Update History lines for a locus JSON, oldest first. Split rows
// come before the locus's own rows on the same date, so a retired split source
// reads "split into ..." then "obsoleted".
function buildUpdateHistoryLines(locus) {
  const page = locus.name;
  const entries = [];
  const splits = {};

  (locus.updateHistories || []).forEach((h) => {
    if (h.updateType === "added from split" && h.name) {
      if (!splits[h.date]) {
        splits[h.date] = [];
        entries.push({ date: h.date, splitDate: h.date });
      }
      splits[h.date].push(h);
    } else {
      entries.push({ date: h.date, line: affectedLine(page, h) });
    }
  });
  (locus.affectedByHistories || []).forEach((h) => {
    entries.push({ date: h.date, line: ownLine(page, h) });
  });

  // Array.prototype.sort is stable, so same-date rows keep the order above.
  entries.sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));

  const seen = new Set();
  const lines = [];
  entries.forEach((e) => {
    const line =
      e.splitDate !== undefined
        ? splitLine(page, !!locus.isObsolete, e.splitDate, splits[e.splitDate])
        : e.line;
    // The DB holds some rows twice (e.g. AT1G29952 is obsoleted twice on
    // 2017-04-05); one line each is enough.
    const key = lineKey(line);
    if (!seen.has(key)) {
      seen.add(key);
      lines.push(line);
    }
  });
  return lines;
}

module.exports = { buildUpdateHistoryLines };
